var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useMemo } from 'react';
import { SimpleForm, FormDataConsumer, TextInput, useCreate, useRefresh, required, CRUD_CREATE, } from 'react-admin';
import { makeStyles } from '@material-ui/core';
import { useForm } from 'react-final-form';
import slugify from 'slugify';
import { useLayer7Notify } from '../../useLayer7Notify';
import { buildDocumentId } from '../../dataProvider/documents';
import { MarkdownInput } from '../../ui';
import { DocumentFormToolbar } from './DocumentFormToolbar';
export const DocumentCreateForm = ({ document, entityType, entityUuid, allDocuments = [], onSaved = () => { }, onCancel = () => { }, }) => {
    const notify = useLayer7Notify();
    const refresh = useRefresh();
    const [create, { loading, error }] = useCreate('documents');
    const handleSave = newDocument => {
        const documentId = buildDocumentId(entityType, entityUuid, newDocument.navtitle, newDocument.locale);
        create({
            payload: { data: Object.assign(Object.assign({}, newDocument), { id: documentId }) },
        }, {
            action: CRUD_CREATE,
            onSuccess: ({ data }) => {
                notify('resources.documents.notifications.create_success');
                refresh();
                onSaved(data);
            },
            onFailure: error => {
                notify(error ||
                    'resources.documents.notifications.create_error', 'error');
            },
        });
    };
    return (React.createElement(DocumentForm, { document: document, loading: loading, error: error, allDocuments: allDocuments, onSave: handleSave, onCancel: onCancel }));
};
export const DocumentForm = (_a) => {
    var { document = {}, loading = false, error = null, allDocuments = [], onSave = () => { }, onCancel = () => { } } = _a, rest = __rest(_a, ["document", "loading", "error", "allDocuments", "onSave", "onCancel"]);
    const classes = useStyles(rest);
    const navtitles = useMemo(() => Object.values(allDocuments).map(item => item.navtitle), [allDocuments]);
    return (React.createElement(SimpleForm, { resource: "documents", record: document, toolbar: React.createElement(DocumentFormToolbar, { loading: loading, error: error, onCancel: onCancel }), save: onSave },
        React.createElement(FormDataConsumer, null, () => {
            // eslint-disable-next-line
            const form = useForm();
            return (React.createElement(React.Fragment, null,
                React.createElement(TextInput, { resource: "documents", source: "title", className: classes.title, onChange: event => {
                        const navtitleFieldState = form.getFieldState('navtitle');
                        if (navtitleFieldState.modified &&
                            navtitleFieldState.touched) {
                            return;
                        }
                        const newNavtitle = slugifyURI(event.target.value);
                        form.change('navtitle', newNavtitle);
                    }, validate: required() }),
                React.createElement(TextInput, { resource: "documents", source: "navtitle", className: classes.navtitle, validate: [
                        required(),
                        checkUnicity(navtitles),
                        checkSpecialCharacters(),
                    ] })));
        }),
        React.createElement(MarkdownInput, { source: "markdown", formClassName: classes.markdown, validate: required(), isRequired: true, fullWidth: true })));
};
const useStyles = makeStyles(theme => ({
    title: {
        display: 'inline-block',
        width: '256px',
    },
    navtitle: {
        display: 'inline-block',
        marginLeft: theme.spacing(4),
        width: '256px',
    },
    markdown: {
        width: '100%',
    },
}), {
    name: 'Layer7DocumentCreateForm',
});
const URI_ALLOWED_STRING = /^[a-zA-Z0-9_-]*$/;
const URI_NOT_ALLOWED_CHARACTERS = /[^a-zA-Z0-9_-\s]/g;
export const checkSpecialCharacters = () => value => {
    if (URI_ALLOWED_STRING.test(value)) {
        return;
    }
    return 'resources.documents.validation.error_no_special_characters';
};
export const checkUnicity = navtitles => value => {
    if (!navtitles
        .map(navtitle => navtitle.toLowerCase())
        .includes(value.toLowerCase())) {
        return;
    }
    return 'resources.documents.validation.error_navtitle_not_unique';
};
const replaceNotAllowedCharacters = (text, replacement = '_') => {
    return text.replace(URI_NOT_ALLOWED_CHARACTERS, replacement);
};
export const slugifyURI = uri => {
    const cleanedURI = replaceNotAllowedCharacters(uri, '_');
    return slugify(cleanedURI);
};
//# sourceMappingURL=DocumentCreateForm.js.map